import type { ProvablyFair } from '@skinstep/shared';
import { type CSSProperties, useState } from 'react';
import { PanelLabel } from './PanelLabel';

type Props = {
  provablyFair: ProvablyFair | null;
  className?: string;
  style?: CSSProperties;
};

function shortHex(hex: string): string {
  if (hex.length <= 12) return hex;
  return `${hex.slice(0, 8)}…${hex.slice(-4)}`;
}

function HashRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'primary' | 'success';
}) {
  const [copied, setCopied] = useState(false);
  const labelClass = tone === 'success' ? 'text-success' : 'text-primary';

  async function copy() {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('provably-fair.copy_failed', err);
    }
  }

  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className={`font-mono text-[10px] tracking-[0.25em] ${labelClass} shrink-0 w-14`}>
        {label}
      </span>
      <span className="font-mono text-[11px] tabular-nums text-fg truncate flex-1">
        {shortHex(value)}
      </span>
      <button
        type="button"
        onClick={copy}
        className="font-mono text-[9px] tracking-[0.2em] uppercase text-fg-muted hover:text-fg transition-colors shrink-0"
      >
        {copied ? 'copied' : 'copy'}
      </button>
    </div>
  );
}

export function ProvablyFairBadge({ provablyFair, className, style }: Props) {
  if (!provablyFair) return null;

  return (
    <section
      className={`mx-3 mt-5 border border-edge bg-bg-elev/30 px-3 py-3${className ? ` ${className}` : ''}`}
      style={style}
    >
      <PanelLabel label="PROVABLY FAIR" right="✓" rightTone="success" className="mb-2" />
      <div className="flex flex-col gap-1.5">
        <HashRow label="COMMIT" value={provablyFair.commit} tone="primary" />
        {provablyFair.revealed ? (
          <HashRow label="SEED" value={provablyFair.revealed.seed} tone="success" />
        ) : (
          <p className="font-mono text-[10px] text-fg-dim leading-snug">
            seed will be revealed when this lot closes
          </p>
        )}
      </div>
      <p className="font-mono text-[9px] text-fg-dim mt-3 leading-relaxed break-words">
        timer extension = HMAC-SHA256(seed, “bid:N”)[0..4] mod 7000ms + 8000ms
      </p>
    </section>
  );
}
